// utils/lnText.ts
//
// Lane screens (Japan, Korea, GCC, Indonesia, Tarot, legal pages, …) are
// authored in English, sometimes with one regional language beside it. `ls`
// translates the English text the user would otherwise see: it looks the
// sentence up under "ln.<slug>_<hash>" in the locale files and falls back to
// the English text itself. Option values and keys sent to the backend are
// never passed through it.
//
//   ls("Hello")                  → translated string
//   L({ title: "A", list: [..] })→ same shape, every sentence translated
//   LC({ en: {...}, ja: {...} }, lang)
//                                → the screen's own language when it is the
//                                  active one, otherwise L(copy.en)
import i18n from "../i18n";
import { useTranslation } from "react-i18next";

const hash = (s: string): string => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(36);
};

export const lnKey = (s: string): string =>
  `ln.${s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 32)}_${hash(s)}`;

export const ls = (s: string): string =>
  typeof s === "string" && s ? i18n.t(lnKey(s), { defaultValue: s }) : s;

// Properties whose strings are identifiers or values, never display text.
const SKIP_KEYS = new Set([
  "value", "key", "id", "code", "slug", "type", "path", "to", "href", "icon",
  "color", "gradient", "className", "route", "endpoint", "subCategoryName",
]);

export function L<T>(node: T): T {
  if (typeof node === "string") return ls(node) as unknown as T;
  if (Array.isArray(node)) return node.map((v) => L(v)) as unknown as T;
  if (node && typeof node === "object" && (node as any).$$typeof === undefined) {
    const out: Record<string, any> = {};
    for (const k of Object.keys(node as any)) {
      const v = (node as any)[k];
      out[k] = SKIP_KEYS.has(k) ? v : L(v);
    }
    return out as T;
  }
  return node;
}

export function LC<T = any>(obj: Record<string, T> | undefined, lang: string): T {
  if (!obj) return undefined as unknown as T;
  if (lang !== "en" && obj[lang] !== undefined) return obj[lang];
  return L(obj.en);
}

/** Subscribes the calling component to language changes. */
export const useLnLanguage = (): string => useTranslation().i18n.language;
